import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router";
import {auth0_domain} from "../../app.config";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import {useFormik} from "formik";
import {MuiTelInput} from "mui-tel-input";
import {validationSchema, organizationValidationSchema} from "../../validations/validations";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import SignupStyles from "../../styles/registrationPageStyles.module.css";
import {Alert, Chip} from "@mui/material";
// import ReCAPTCHA from "react-google-recaptcha";
import {GoogleReCaptcha, GoogleReCaptchaProvider} from "react-google-recaptcha-v3";
import {signupUser} from "../../network/apis/auth.api";
import {useAuth0} from "@auth0/auth0-react";
import {jwtDecode} from 'jwt-decode';
import CircularProgress from "@mui/material/CircularProgress";

const RegistrationForm = (props) => {
    const {logout} = useAuth0();
    const navigate = useNavigate();
    const location = useLocation();
    const [phoneNumberValue, setPhoneNumberValue] = useState("");
    const values = ["Part 107", "44809"];
    const [errorMessage, setErrorMessage] = useState(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
    const [auth0Domain, setAuth0Domain] = useState('');
    const [stateParam, setStateParam] = useState('');
    const queryParams = new URLSearchParams(window.location.search);
    const userData = JSON.parse(decodeURIComponent(queryParams.get('data')));
    const nextPath = queryParams.get('next'); // Get next redirect path for organization signup
    const isOrganizationSignup = nextPath === '/signup/organization';

    const initialValues = {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        email: userData?.email || "",
        phoneNumber: "",
        faaIDType: "",
        faaIDValue: "",
    };
    const auth0UserId = userData?.userId || "";
    const loginMethod = userData?.loginMethod || "";



    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionToken = params.get('session_token');
        const state = params.get('state');

        if (!sessionToken || !state) {
            //setError('Missing required authentication parameters');
            //setLoading(false);
            return;
        }

        try {
            // Decode the token (without verification for display only)
            const decoded = jwtDecode(sessionToken);

            // Extract Auth0 domain from issuer
            if (decoded.iss) {
                setAuth0Domain(decoded.iss.replace('https://', ''));
            }

            setStateParam(state);
            //setLoading(false);
        } catch (err) {
            //setError('Invalid session token');
            //setLoading(false);
        }
    }, [location.search]);

    const formik = useFormik({
        initialValues,
        validationSchema: isOrganizationSignup ? organizationValidationSchema : validationSchema,
        onSubmit: async (values, {setSubmitting}) => {

            if (!auth0Domain || !stateParam) {
                //setError('Cannot complete login - missing parameters');
                return;
            }
            if (!recaptchaToken) {
                setErrorMessage("Please complete the reCAPTCHA verification.");
                setSubmitting(false);
                return;
            }

            try {

                const finalPayload = {
                    ...values,
                    auth0UserId,
                    loginMethod,
                };
                const user = await signupUser({...finalPayload, recaptchaToken});

                // Store next path in sessionStorage before Auth0 redirect
                if (nextPath) {
                    sessionStorage.setItem('auth0RedirectTo', nextPath);
                }

                // Complete Auth0 flow
                window.location.href = `https://${auth0_domain}/continue?state=${stateParam}`;

            } catch (err) {
                //console.log(err);
                if (err.response) {
                    const errorSum = err.response.data.error;
                    setRefreshReCaptcha(!refreshReCaptcha);
                    // Handle specific error cases
                    if (
                        errorSum ===
                        "User already exists"
                    ) {
                        setErrorMessage("User already exists with this email");
                    } else {
                        setErrorMessage("Form Error. Please contact Flyzone team for support");
                    }
                } else {
                    setErrorMessage("Form Error. Please contact Flyzone team for support");
                }
            } finally {
                setSubmitting(false);
            }
        },
    });


    const {
        values: formData,
        errors,
        touched,
        handleChange,
        handleSubmit,
    } = formik;
    //console.log('formdata',formData)

    const [sessionToken, setSessionToken] = useState(null);

    // useEffect(() => {
    //     if (authState?.getIdToken) {
    //         setSessionToken(authState.getIdToken());
    //     }
    // }, [authState]);

    const onRecaptchaChange = (token) => {
        setRecaptchaToken(token);
    };

    if (sessionToken) {
        navigate("/confirmation-email");
        return null;
    }

    const errorOccured = errorMessage ? (
        <span>{errorMessage}</span>
    ) : null;

    return (
        <>
            <Container component="main" maxWidth="sm" className={SignupStyles.container}>
                <Box className={SignupStyles.formBox}>
                    {/* Header Section */}
                    <Box className={SignupStyles.header}>
                        <Typography variant="h6" className={SignupStyles.headerTitle}>
                            ACCOUNT DETAILS
                        </Typography>
                    </Box>

                    {/* Form Section */}
                    <Box component="form" noValidate onSubmit={handleSubmit}>
                        {errorOccured && (
                            <Alert severity="error" fullWidth>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                    {errorMessage}
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="error"
                                        onClick={() =>
                                            logout({
                                                logoutParams: { returnTo: window.location.origin },
                                            })
                                        }
                                        className={SignupStyles.errorActionButton}
                                        classes={{ sizeSmall: SignupStyles.errorActionButtonSmall }}
                                    >
                                        {errorMessage === "User already exists with this email"
                                            ? "Use different credentials"
                                            : "Login again"}
                                    </Button>
                                </span>
                            </Alert>
                        )}
                        {/* Personal Information Section */}
                        <Box className={SignupStyles.section}>
                            <Typography variant="subtitle1" className={SignupStyles.sectionTitle}>
                                Personal Information
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        variant="outlined"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={touched.firstName && Boolean(errors.firstName)}
                                        helperText={touched.firstName && errors.firstName}
                                        InputProps={{
                                            className: SignupStyles.textFieldInput
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        variant="outlined"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        error={touched.lastName && Boolean(errors.lastName)}
                                        helperText={touched.lastName && errors.lastName}
                                        InputProps={{
                                            className: SignupStyles.textFieldInput
                                        }}

                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Contact Information Section */}
                        <Box className={SignupStyles.section}>
                            <Typography variant="subtitle1" className={SignupStyles.sectionTitle}>
                                Contact Information
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        variant="outlined"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={!!formData.email}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                        InputProps={{
                                            className: SignupStyles.textFieldInput
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MuiTelInput
                                        fullWidth
                                        defaultCountry="US"
                                        onlyCountries={["US"]}
                                        forceCallingCode
                                        label="Phone Number"
                                        name="phoneNumber"
                                        value={phoneNumberValue}
                                        onChange={(value) => {
                                            handleChange({target: {name: "phoneNumber", value}});
                                            setPhoneNumberValue(value);
                                        }}
                                        error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                        helperText={touched.phoneNumber && errors.phoneNumber}
                                        inputProps={{
                                            className: SignupStyles.textFieldInput
                                        }}
                                        InputLabelProps={{
                                            sx: {

                                                fontSize: "20px",
                                            }
                                        }}

                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* FAA Information Section */}
                        <Box className={SignupStyles.section}>
                            <Typography variant="subtitle1" className={SignupStyles.sectionTitle}>
                                FAA Information {isOrganizationSignup && '(Optional for Organization Admins)'}
                            </Typography>
                            {isOrganizationSignup && (
                                <Typography variant="body2" style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                                    You can skip this section if you won't be personally flying drones. Team members who fly will provide their FAA details when they join.
                                </Typography>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel className={SignupStyles.selectLabel}>FAA ID Type</InputLabel>
                                        <Select
                                            label="FAA ID Type"
                                            name="faaIDType"
                                            value={formData.faaIDType}
                                            onChange={handleChange}
                                            error={touched.faaIDType && Boolean(errors.faaIDType)}
                                            className={SignupStyles.selectField}
                                            MenuProps={{
                                                className: SignupStyles.selectMenu
                                            }}
                                        >
                                            {values.map((data) => (
                                                <MenuItem key={data} value={data} className={SignupStyles.menuItem}>
                                                    {data}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="FAA License ID (Remote Pilot/Trust Certificate ID)"
                                        variant="outlined"
                                        name="faaIDValue"
                                        value={formData.faaIDValue}
                                        onChange={handleChange}
                                        error={touched.faaIDValue && Boolean(errors.faaIDValue)}
                                        helperText={touched.faaIDValue && errors.faaIDValue}
                                        InputProps={{
                                            className: SignupStyles.textFieldInput
                                        }}
                                        InputLabelProps={{
                                            style: {
                                                fontSize: '12px' // Set the label font size to a smaller size
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {/*<ReCAPTCHA*/}
                                    {/*sitekey="6LcWRv0pAAAAAG64mHixB5DQ2uqUoY7uoQMOWM6a"*/}
                                    {/*onChange={onRecaptchaChange}*/}
                                    {/*/>*/}
                                    <GoogleReCaptchaProvider reCaptchaKey="6LcWRv0pAAAAAG64mHixB5DQ2uqUoY7uoQMOWM6a">
                                        <GoogleReCaptcha
                                            onVerify={onRecaptchaChange}
                                            refreshReCaptcha={refreshReCaptcha}
                                        />
                                    </GoogleReCaptchaProvider>

                                </Grid>
                            </Grid>
                        </Box>

                        {/* Terms and Actions Section */}
                        <Box className={SignupStyles.termsSection}>
                            <Typography variant="body2" className={SignupStyles.termsText}>
                                By registering, you agree to Flyzone's{' '}
                                <Link href="/terms" underline="hover"
                                      className={SignupStyles.link}>Terms</Link> and{' '}
                                <Link href="/privacy" underline="hover" className={SignupStyles.link}>Privacy
                                    Policy</Link>
                            </Typography>

                            <Box className={SignupStyles.buttonContainer}>
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    className={SignupStyles.submitButton}
                                    disabled={formik.isSubmitting} // Disable button when submitting
                                >
                                    {formik.isSubmitting ? (
                                        <CircularProgress size={24} sx={{color: "white"}}/>
                                    ) : (
                                        "Save & Continue"
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default RegistrationForm;